/**
 * ModerationQueuePage — Round 13 (Sep 12 2026), App Store guideline 1.2.
 * ONE queue for everything users report (posts, comments, live chat
 * messages, lives, DMs, profiles) and everything the text filter refused.
 * Every row shows who, what, the content snapshot and how many people
 * reported it; the actions are the ones an operator needs inside 24h:
 * dismiss, remove the content, warn, suspend (7d), ban, mute in the live,
 * end the live. Each action resolves the report and lands in the audit log.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Flag, RefreshCw, Trash2, EyeOff, Eye, AlertTriangle, Ban, UserX, MicOff, Video, CheckCircle, XCircle, Clock, ShieldAlert,
} from 'lucide-react';

const TYPE_LABEL = { user: 'Profile', post: 'Post', comment: 'Comment', chat_message: 'Live chat', stream: 'Live', dm: 'Direct message' };
const STATUS_TABS = [
  { id: 'open', label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'all', label: 'All' },
];

const ago = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export function ModerationQueue({ embedded = false }) {
  const { api } = useAdmin();
  const [status, setStatus] = useState('open');
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({ open: 0, resolved: 0, dismissed: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/admin/moderation/reports?status=${status}&limit=200`);
      setReports(r.data?.reports || []);
      setCounts(r.data?.counts || { open: 0, resolved: 0, dismissed: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the queue');
    } finally {
      setLoading(false);
    }
  }, [api, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (action, extra = {}) => {
    if (!selected) return;
    const confirmText = {
      delete_content: 'Permanently remove this content?',
      ban: `Permanently BAN ${selected.target_user_name || 'this user'}? They lose access immediately.`,
      suspend: `Suspend ${selected.target_user_name || 'this user'} for 7 days? They lose access immediately.`,
      end_stream: 'End this live for everyone watching?',
    }[action];
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try {
      const r = await api.post(`/api/admin/moderation/reports/${selected.id}/action`, { action, notes, ...extra });
      toast.success(r.data?.message === 'Done' ? `${action.replace('_', ' ')} — done` : r.data?.message);
      setSelected(null);
      setNotes('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const ActionBtn = ({ icon: Icon, label, onClick, tone = 'gray', disabled }) => {
    const tones = {
      gray: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      red: 'bg-rose-600 text-white hover:bg-rose-700',
      amber: 'bg-amber-100 text-amber-800 hover:bg-amber-200',
      green: 'bg-emerald-600 text-white hover:bg-emerald-700',
    };
    return (
      <button
        onClick={onClick}
        disabled={busy || disabled}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 ${tones[tone]}`}
      >
        <Icon className="w-4 h-4" />
        {label}
      </button>
    );
  };

  const isContent = selected && ['post', 'comment', 'chat_message', 'dm'].includes(selected.target_type);
  const hasUser = selected && !!selected.target_user_id;
  const hasStream = selected && (selected.stream_id || selected.target_type === 'stream');

  return (
    <div className={embedded ? '' : 'p-6 max-w-7xl mx-auto'} data-testid="moderation-queue-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`${embedded ? 'text-lg' : 'text-2xl'} font-bold text-gray-900 flex items-center gap-2`}><Flag className="w-5 h-5 text-rose-600" /> Reports Queue</h1>
          <p className="text-gray-500 text-sm mt-1">Everything users report plus everything the text filter refused. Apple and Google expect action within 24 hours.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {[['Open', counts.open, 'text-rose-600'], ['Resolved', counts.resolved, 'text-emerald-600'], ['Dismissed', counts.dismissed, 'text-gray-500']].map(([label, n, cls]) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
            <div className={`text-3xl font-bold mt-1 ${cls}`}>{n}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-4">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => { setStatus(t.id); setSelected(null); }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${status === t.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            data-testid={`queue-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* list */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading && reports.length === 0 ? (
            <div className="p-10 text-center text-gray-400">Loading…</div>
          ) : reports.length === 0 ? (
            <div className="p-10 text-center text-gray-400">Nothing here. 🎉</div>
          ) : (
            <ul className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto">
              {reports.map((r) => (
                <li
                  key={r.id}
                  onClick={() => { setSelected(r); setNotes(''); }}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${selected?.id === r.id ? 'bg-emerald-50' : ''}`}
                  data-testid={`report-row-${r.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {r.priority === 'high' && <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0" title="High priority" />}
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700">{TYPE_LABEL[r.target_type] || r.target_type}</span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-rose-50 text-rose-700">{r.reason_label || r.reason}</span>
                      {r.reports_on_target > 1 && <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700">×{r.reports_on_target} reports</span>}
                      {r.source === 'filter' && <span className="text-xs px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">auto-filter</span>}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0 flex items-center gap-1"><Clock className="w-3 h-3" />{ago(r.created_at)}</span>
                  </div>
                  <div className="mt-1.5 text-sm text-gray-900 font-medium truncate">
                    {r.target_user_name || r.target_user_id || 'Unknown user'}
                    {r.target_user_handle ? <span className="text-gray-400 font-normal"> @{r.target_user_handle}</span> : null}
                    {r.target_user?.is_suspended || r.target_user?.is_banned ? <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">{r.target_user.is_banned || r.target_user.suspension_type === 'permanent' ? 'banned' : 'suspended'}</span> : null}
                  </div>
                  {r.snapshot ? <div className="mt-1 text-sm text-gray-600 line-clamp-2">“{r.snapshot}”</div> : null}
                  <div className="mt-1 text-xs text-gray-400">reported by {r.reporter_name || r.reporter_id}{r.status !== 'open' ? ` · ${r.status}${r.action_taken ? ` (${r.action_taken.replace('_', ' ')})` : ''}` : ''}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* detail + actions */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5 h-fit sticky top-4">
          {!selected ? (
            <div className="text-center text-gray-400 py-16">Select a report to review it</div>
          ) : (
            <div data-testid="report-detail">
              <div className="text-xs text-gray-500 uppercase tracking-wide">{TYPE_LABEL[selected.target_type] || selected.target_type} · {selected.reason_label || selected.reason}{selected.priority === 'high' ? ' · HIGH' : ''}</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">{selected.target_user_name || selected.target_user_id || 'Unknown user'}</div>
              {selected.target_user_handle && <div className="text-sm text-gray-500">@{selected.target_user_handle}</div>}
              {selected.target_user && (
                <div className="mt-1 text-xs text-gray-500">warnings: {selected.target_user.warnings_count || 0}{selected.target_user.is_suspended ? ' · currently suspended' : ''}</div>
              )}
              {selected.snapshot ? (
                <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-800 whitespace-pre-wrap">“{selected.snapshot}”</div>
              ) : null}
              {selected.details ? <div className="mt-2 text-sm text-gray-600"><span className="font-medium">Reporter says:</span> {selected.details}</div> : null}
              <div className="mt-2 text-xs text-gray-400">Reported by {selected.reporter_name || selected.reporter_id} · {new Date(selected.created_at).toLocaleString()}{selected.reports_on_target > 1 ? ` · ${selected.reports_on_target} reports on this` : ''}</div>

              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes for the audit log (optional)"
                className="mt-4 w-full border border-gray-200 rounded-lg p-2 text-sm"
                rows={2}
                data-testid="report-notes"
              />

              {selected.status === 'open' ? (
                <div className="mt-4 space-y-3">
                  {isContent && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Content</div>
                      <div className="flex flex-wrap gap-2">
                        <ActionBtn icon={Trash2} label="Remove content" tone="red" onClick={() => act('delete_content')} />
                        <ActionBtn icon={EyeOff} label="Hide" onClick={() => act('hide_content')} />
                        <ActionBtn icon={Eye} label="Unhide" onClick={() => act('unhide_content')} />
                      </div>
                    </div>
                  )}
                  {hasUser && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">User</div>
                      <div className="flex flex-wrap gap-2">
                        <ActionBtn icon={AlertTriangle} label="Warn" tone="amber" onClick={() => act('warn')} />
                        <ActionBtn icon={UserX} label="Suspend 7 days" tone="amber" onClick={() => act('suspend', { days: 7 })} />
                        <ActionBtn icon={Ban} label="Ban" tone="red" onClick={() => act('ban')} />
                      </div>
                    </div>
                  )}
                  {hasStream && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Live</div>
                      <div className="flex flex-wrap gap-2">
                        {hasUser && selected.stream_id && <ActionBtn icon={MicOff} label="Mute in this live" onClick={() => act('mute')} />}
                        <ActionBtn icon={Video} label="End the live" tone="red" onClick={() => act('end_stream')} />
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Close</div>
                    <div className="flex flex-wrap gap-2">
                      <ActionBtn icon={CheckCircle} label="Resolve (handled)" tone="green" onClick={() => act('resolve')} />
                      <ActionBtn icon={XCircle} label="Dismiss" onClick={() => act('dismiss')} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-4 text-sm text-gray-500">This report is {selected.status}{selected.action_taken ? ` — ${selected.action_taken.replace('_', ' ')}` : ''}{selected.admin_notes ? `. Notes: ${selected.admin_notes}` : ''}.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ModerationQueuePage() {
  return <ModerationQueue />;
}
